import { Viewport } from "../../../pixel-accessor";
import type { Point } from "../../../types";
import type {
  EwaReversePixelMapper,
  PartialDerivatives,
} from "../../EwaReversePixelMapper";
import type { BestFitReversePixelMapper } from "../../BestFitReversePixelMapper";
import type { ForwardPixelMapper } from "../../ForwardPixelMapper";
import { perceptibleReciprocal } from "../../../utils";
import { InvalidArgument } from "../../../exception";

// prettier-ignore
/**
 * Perspective matrix:
 * [ sx, ry, tx,
 *   rx, sy, ty,
 *   px, py, 1 ].
 */
export type PerspectiveMatrix = [
  number, number, number,
  number, number, number,
  number, number
];

/**
 * Returns inverted perspective matrix.
 *
 * @param matrix
 *
 * @see https://imagemagick.org/api/MagickCore/distort_8c_source.html#l00109 Inverting perspective matrix at ImageMagick source
 */
export function invertPerspectiveMatrix(
  matrix: PerspectiveMatrix
): PerspectiveMatrix {
  const determinant = perceptibleReciprocal(
    matrix[0] * matrix[4] - matrix[3] * matrix[1]
  );

  if (!determinant) {
    throw new InvalidArgument("Given matrix can't be inverted");
  }

  return [
    determinant * (matrix[4] - matrix[7] * matrix[5]),
    determinant * (matrix[7] * matrix[2] - matrix[1]),
    determinant * (matrix[1] * matrix[5] - matrix[4] * matrix[2]),
    determinant * (matrix[6] * matrix[5] - matrix[3]),
    determinant * (matrix[0] - matrix[6] * matrix[2]),
    determinant * (matrix[3] * matrix[2] - matrix[0] * matrix[5]),
    determinant * (matrix[3] * matrix[7] - matrix[6] * matrix[4]),
    determinant * (matrix[6] * matrix[1] - matrix[0] * matrix[7]),
  ];
}

/**
 * Project coordinates using perspective projection matrix.
 *
 * @param x
 * @param y
 * @param matrix
 */
export function applyPerspectiveMatrix(
  x: number,
  y: number,
  matrix: PerspectiveMatrix
): [number, number] {
  const p = matrix[0] * x + matrix[1] * y + matrix[2],
    q = matrix[3] * x + matrix[4] * y + matrix[5],
    r = matrix[6] * x + matrix[7] * y + 1;

  return [p / r, q / r];
}

/**
 * Perspective Distortion (a ratio of affine distortions).
 *
 * ```
 *     p(x,y)    c0*x + c1*y + c2
 * u = ------ = ------------------
 *     r(x,y)    c6*x + c7*y + 1
 *
 *     q(x,y)    c3*x + c4*y + c5
 * v = ------ = ------------------
 *      r(x,y)    c6*x + c7*y + 1
 * ```
 *
 * denominator = Sign of 'r', or the denominator affine, for the actual image.
 * This determines what part of the distorted image is 'ground' side of the horizon, the other part is 'sky' or invalid.
 * Valid values are  +1.0  or  -1.0  only.
 *
 *
 * @see https://www.imagemagick.org/Usage/distorts/#perspective Perspective distortion details at ImageMagick docs
 * @see https://imagemagick.org/api/MagickCore/distort_8c_source.html#l02450 Perspective distortion at ImageMagick source
 */
export class Perspective
  implements
    EwaReversePixelMapper,
    BestFitReversePixelMapper,
    ForwardPixelMapper
{
  /**
   * Reverse matrix.
   */
  readonly matrix: PerspectiveMatrix;

  /**
   * Denominator for mapping validity calculation.
   */
  readonly denominator: number;

  /**
   * Forward matrix.
   */
  readonly forwardMatrix: PerspectiveMatrix;

  /**
   * @inheritDoc
   */
  readonly isConstantPartialDerivatives = false;

  /**
   * Cached Math.abs(matrix[6])
   * @private
   */
  private absC6: number;

  /**
   * Cached Math.abs(matrix[7])
   * @private
   */
  private absC7: number;

  /**
   * Perspective constructor.
   *
   * @param reverseMatrix Perspective projection matrix for reverse pixel mapping.
   * @param denominator Sign of 'r', or the denominator affine, for the actual image.
   * This determines what part of the distorted image is 'ground' side of the horizon, the other part is 'sky' or invalid.
   * Valid values are  +1.0  or  -1.0  only.
   */
  constructor(reverseMatrix: PerspectiveMatrix, denominator: number) {
    this.matrix = reverseMatrix;
    this.denominator = denominator;
    this.forwardMatrix = invertPerspectiveMatrix(reverseMatrix);
    this.absC6 = Math.abs(reverseMatrix[6]);
    this.absC7 = Math.abs(reverseMatrix[7]);
  }

  /**
   * Creates Perspective instance using perspective matrix.
   *
   * @param matrix Perspective matrix.
   *
   * @see https://imagemagick.org/api/MagickCore/distort_8c_source.html#l00853 Generating inverted perspective
   * distortion matrix from forward perspective matrix at ImageMagick docs
   */
  static fromForwardMatrix(matrix: PerspectiveMatrix): Perspective {
    const inverse = invertPerspectiveMatrix(matrix);

    /*
     * Calculate denominator! The ground-sky determination.
     * What is sign of the 'ground' in r() denominator affine function?
     * Just use any valid image coordinate in destination for determination.
     * For a forward mapped perspective the images 0,0 coord will map to
     * c2,c5 in the distorted image, so set the sign of denominator of that.
     */
    const denominator =
      inverse[6] * matrix[2] + inverse[7] * matrix[5] + 1 < 0 ? -1 : 1;
    return new Perspective(inverse, denominator);
  }

  /**
   * @inheritDoc
   */
  reverseMap(x: number, y: number): Point {
    const p = this.matrix[0] * x + this.matrix[1] * y + this.matrix[2],
      q = this.matrix[3] * x + this.matrix[4] * y + this.matrix[5],
      r = this.matrix[6] * x + this.matrix[7] * y + 1;

    return [p / r, q / r];
  }

  /**
   * @inheritDoc
   */
  getValidity(x: number, y: number, scaling: number): number {
    const r = this.matrix[6] * x + this.matrix[7] * y + 1;
    let validity = r * this.denominator < 0 ? 0 : 1;
    const absR = Math.abs(r) * 2;

    if (this.absC6 > this.absC7) {
      if (absR < this.absC6) {
        validity = 0.5 - (this.denominator * r) / (this.matrix[6] * scaling);
      }
    } else if (absR < this.absC7) {
      validity = 0.5 - (this.denominator * r) / (this.matrix[7] * scaling);
    }

    return validity;
  }

  /**
   * @inheritDoc
   */
  getPartialDerivatives(x: number, y: number): PartialDerivatives {
    const p = this.matrix[0] * x + this.matrix[1] * y + this.matrix[2],
      q = this.matrix[3] * x + this.matrix[4] * y + this.matrix[5],
      r = this.matrix[6] * x + this.matrix[7] * y + 1,
      scale = Math.pow(1 / r, 2);

    return [
      (r * this.matrix[0] - p * this.matrix[6]) * scale, // dUx
      (r * this.matrix[1] - p * this.matrix[7]) * scale, // dUy
      (r * this.matrix[3] - q * this.matrix[6]) * scale, // dVx
      (r * this.matrix[4] - q * this.matrix[7]) * scale, //dVy
    ];
  }

  /**
   * @inheritDoc
   */
  forwardMap(u: number, v: number): Point {
    return applyPerspectiveMatrix(u, v, this.forwardMatrix);
  }

  /**
   * @inheritDoc
   */
  getBestFitViewport(viewport: Viewport): Viewport {
    const u1 = viewport.x1,
      v1 = viewport.y1,
      u2 = viewport.x2 + 1,
      v2 = viewport.y2 + 1,
      [x, y] = this.forwardMap(u1, v1),
      bestFit = new Viewport(x, y, x, y);

    (
      [
        [u2, v1],
        [u2, v2],
        [u1, v2],
      ] as [number, number][]
    ).forEach((apex: [number, number]) =>
      bestFit.expand(...this.forwardMap(...apex))
    );

    bestFit.fixBounds();

    return bestFit;
  }
}
